import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { MaskedTextField, NumberFieldWithBackground } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

// ─────────────────────────────────────────────────────────────────────────────

export const TipsGroupCustomTip = (props: TipsGroupCustomTipProps) => {
  const { selectedTip, maxTipAmount, onChange } = props;

  const { formatMessage, formatNumber } = useIntl();
  const isUpdatedInputEnabled = useFeatureFlag('CELS-4378-order-updated-tip-input-enabled'); // prettier-ignore

  // NOTE: We use cents for tip calculations and dollar values for presentation
  const displayTipAmount = selectedTip / 100;
  const displayTipAmountV2 = String(selectedTip);

  const hasExceedMaxTipAmount = selectedTip > maxTipAmount;

  const customTipNoticeText = formatMessage(messages.maximumReached, {
    max: formatNumber(maxTipAmount / 100, {
      style: 'currency',
      currency: 'USD',
    }),
  });

  const customTipNotice = hasExceedMaxTipAmount ? customTipNoticeText : undefined;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onCustomTipAmountChange = useCallback(
    (tipAmountNumericValue: string) => {
      const tipAmountAsNumber = Number(tipAmountNumericValue);
      const isValidTipAmount = validateCustomTipAmount(tipAmountAsNumber);

      const tipAmountAsNumberInCents = tipAmountAsNumber * 100;
      const tipAmountWithFallback = isValidTipAmount ? tipAmountAsNumberInCents : 0;

      onChange(tipAmountWithFallback);
    },
    [onChange],
  );

  const onCustomTipAmountChangeV2 = useCallback(
    (_text: string, tipAmountNumericValue: string) => {
      const tipAmountAsNumber = Number(tipAmountNumericValue);
      const isValidTipAmount = validateCustomTipAmount(tipAmountAsNumber);

      const tipAmountAsNumberInCents = tipAmountAsNumber;
      const tipAmountWithFallback = isValidTipAmount ? tipAmountAsNumberInCents : 0;

      onChange(tipAmountWithFallback);
    },
    [onChange],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (isUpdatedInputEnabled) {
    return (
      <MaskedTextField.Currency
        accessibilityLabel={formatMessage(messages.customField)}
        placeholder={formatMessage(messages.customField)}
        value={selectedTip <= 0 ? '' : displayTipAmountV2}
        onChangeText={onCustomTipAmountChangeV2}
        noticePalette="caution"
        invalid={hasExceedMaxTipAmount}
        notice={customTipNotice}
        autoFocus
        returnKeyType="done"
      />
    );
  }

  return (
    <NumberFieldWithBackground
      accessibilityLabel={formatMessage(messages.customField)}
      placeholder={formatMessage(messages.customField)}
      value={displayTipAmount <= 0 ? undefined : displayTipAmount}
      onChangeText={onCustomTipAmountChange}
      min={0}
      maxDecimals={2}
      invalid={hasExceedMaxTipAmount}
      noticePalette="caution"
      notice={customTipNotice}
      textPrepend="$"
      autoFocus
      returnKeyType="done"
    />
  );
};

// ─── Utils ───────────────────────────────────────────────────────────────────

function validateCustomTipAmount(tipAmount: number) {
  return Number.isFinite(tipAmount) && tipAmount > 0;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  customField: {
    defaultMessage: 'Enter custom tip',
    description: 'Bag | Tipping | Custom field',
  },
  maximumReached: {
    defaultMessage:
      'The maximum tip for this order is {max}, but our team appreciates your generosity!',
    description: 'Bag | Tipping | Maximum reached',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type TipsGroupCustomTipProps = Readonly<{
  selectedTip: number;
  maxTipAmount: number;
  onChange: (topAmount: number) => void;
}>;
